import { edgeLabel } from "./graph";
import { TransitionSummary } from "./TransitionSummary";
import type { EditorMetadata, Transition } from "./types";

/**
 * The transitions as a keyboard-navigable list, the selected one expanded into a readable
 * summary of what it does.
 *
 * Not a fallback for the graph — it is the non-mouse route to every transition, and it reads in
 * the order the engine evaluates. The expansion sits here, directly under the diagram, because
 * "what is this configured to do" is a reading task; the panel on the right is a set of controls
 * for changing it, which is a different job and a bad shape for answering the question.
 */
export function TransitionList({
    transitions,
    metadata,
    selectedTransitionId,
    onSelectTransition,
}: {
    transitions: Transition[];
    metadata: EditorMetadata;
    selectedTransitionId: string | null;
    onSelectTransition: (transition: Transition) => void;
}) {
    if (!transitions.length) {
        return null;
    }

    return (
        <nav
            className="aer-wf-list"
            aria-label="Transitions in evaluation order"
        >
            <h3 className="aer-wf-list__title">Transitions</h3>
            <ol>
                {transitions.map((transition) => {
                    const isSelected = transition.id === selectedTransitionId;
                    return (
                        <li key={transition.id}>
                            <button
                                type="button"
                                className={`aer-wf-list__item${
                                    isSelected
                                        ? " aer-wf-list__item--selected"
                                        : ""
                                }`}
                                aria-current={isSelected ? "true" : undefined}
                                aria-expanded={isSelected}
                                onClick={() => onSelectTransition(transition)}
                            >
                                <span className="aer-wf-list__statuses">
                                    {transition.fromStatus} →{" "}
                                    {transition.toStatus}
                                </span>
                                <span className="aer-wf-list__meta">
                                    {edgeLabel(transition)}
                                </span>
                            </button>

                            {isSelected && (
                                <TransitionSummary
                                    transition={transition}
                                    metadata={metadata}
                                />
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}
